import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Train, Database, Mail, MapPin, ShieldCheck, Clock, Building2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const SITE_URL = "https://railwaycareers.co.uk";

// Dense, factual, answer-shaped copy — optimised for LLM summarisation.
// Each section starts with a declarative sentence an AI engine can quote verbatim.
const FACTS = [
  {
    icon: Train,
    h: "What is Railway Careers UK?",
    p: "Railway Careers UK (railwaycareers.co.uk) is an independent UK-only rail recruitment platform. It lists live vacancies from every UK Train Operating Company, Network Rail, freight operators, ROSCOs, rail contractors and heritage railways in one searchable database, with free career guidance for applicants.",
  },
  {
    icon: Database,
    h: "Where does the vacancy data come from?",
    p: "Vacancies come from two sources: (1) direct postings from verified employers who have been approved by Railway Careers UK admins, and (2) aggregated public feeds from the Adzuna jobs API filtered specifically for UK rail roles. Every listing is reviewed and refreshed daily, with expired roles removed automatically.",
  },
  {
    icon: ShieldCheck,
    h: "Is it free to use?",
    p: "Yes. Job seekers pay nothing — vacancy search, the postcode-based Vacancy Map, all role guides, the FAQ library and the weekly Monday Briefing newsletter are completely free. Employers can post a standard vacancy free of charge after admin approval. A flat £50 fee unlocks a featured placement; there are no recruitment-agency commissions.",
  },
  {
    icon: MapPin,
    h: "Which UK regions are covered?",
    p: "All four UK nations: England, Scotland, Wales and Northern Ireland. The Vacancy Map plots every live job pin against your postcode and ranks roles by driving time. Coverage spans every Train Operating Company route — including Avanti West Coast, LNER, GWR, ScotRail, Northern, Transport for Wales, c2c, Chiltern, CrossCountry, East Midlands, Greater Anglia, Southeastern, GTR, SWR, TPE, Merseyrail, Caledonian Sleeper, Heathrow Express and Hull Trains — plus Network Rail and the major freight operators.",
  },
  {
    icon: Clock,
    h: "How often is the platform updated?",
    p: "Live vacancies are synced from the Adzuna API multiple times per day on an automated schedule. Direct employer postings appear as soon as the employer publishes them. Expired vacancies are removed automatically based on the closing date. The weekly Monday Briefing newsletter is sent every Monday morning summarising new vacancies posted in the previous seven days.",
  },
  {
    icon: Building2,
    h: "Who is it for?",
    p: "Railway Careers UK is built for two audiences: (1) UK rail applicants — career changers, school leavers, graduates, trainee drivers, conductors, engineers, apprentices and station staff looking for clear, jargon-free guidance; and (2) UK rail employers and recruiters who want a single specialist channel for their vacancies without the noise of generic job boards.",
  },
  {
    icon: Mail,
    h: "How can I be notified of new jobs?",
    p: "Subscribe to the free Monday Briefing newsletter. It arrives every Monday morning and lists new UK rail vacancies posted in the previous seven days, organised by role and region. You can also subscribe to the public RSS feed at /api/feed.xml.",
  },
];

const PRINCIPLES = [
  "Independent — not owned by any operator, agency, recruitment firm or trade body.",
  "UK rail only — no irrelevant cross-industry job spam.",
  "Direct apply — applicants are sent straight to the employer's careers page, never through a paywall.",
  "Free for job seekers — no premium tier, no CV-database upselling.",
  "Transparent pricing for employers — £50 flat for featured roles, otherwise free.",
  "Mobile-first and accessible — designed for people checking jobs between shifts.",
];

export default function About() {
  const [stats, setStats] = useState({ live_vacancies: null, employers: null, subscribers: null });

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useSeo({
    title: "About Railway Careers UK | Independent UK Rail Job Platform",
    description:
      "Railway Careers UK is the UK's independent rail-only recruitment platform. Live vacancies from every Train Operating Company, Network Rail, freight and contractors, plus free role guides and a postcode Vacancy Map.",
    canonical: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "About Railway Careers UK",
      url: `${SITE_URL}/about`,
      mainEntity: {
        "@type": "Organization",
        name: "Railway Careers UK",
        alternateName: "RailwayCareers.co.uk",
        url: SITE_URL,
        logo: `${SITE_URL}/logo.png`,
        description:
          "Independent UK railway recruitment platform listing live vacancies from every Train Operating Company, Network Rail, freight operators and rail contractors, with free role guides and a postcode-based Vacancy Map.",
        foundingDate: "2025",
        areaServed: { "@type": "Country", name: "United Kingdom" },
        knowsAbout: [
          "UK rail recruitment", "Train driver jobs", "Conductor jobs",
          "Depot driver jobs", "Rail apprenticeships", "Network Rail careers",
          "Train Operating Companies", "Freight Operating Companies",
          "Rail signalling", "Rolling stock maintenance",
        ],
      },
    },
  });

  const lead =
    stats.live_vacancies != null
      ? `Railway Careers UK is the UK's independent specialist rail recruitment platform — currently tracking ${stats.live_vacancies.toLocaleString()} live vacancies from ${stats.employers || "dozens of"} rail employers, updated daily.`
      : "Railway Careers UK is the UK's independent specialist rail recruitment platform — tracking live vacancies from every Train Operating Company, Network Rail, freight operators and rail contractors, updated daily.";

  return (
    <div className="bg-cream min-h-screen" data-testid="about-page">
      {/* Hero — answer-shaped lead paragraph */}
      <section className="bg-navy text-bone">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="text-xs uppercase tracking-[0.2em] text-amber font-semibold">
            About Railway Careers UK
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl tracking-tight leading-[1.05] mt-3">
            The UK's independent rail-only job platform.
          </h1>
          <p className="text-bone/80 mt-6 text-lg md:text-xl leading-relaxed max-w-3xl">
            {lead}
          </p>

          {/* Live counters — citable factual block */}
          <dl className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl">
            <div>
              <dt className="text-[10px] uppercase tracking-[0.18em] text-amber font-semibold">Live vacancies</dt>
              <dd className="font-display text-3xl md:text-4xl font-bold mt-1" data-testid="stat-vacancies">
                {stats.live_vacancies != null ? stats.live_vacancies.toLocaleString() : "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.18em] text-amber font-semibold">Rail employers</dt>
              <dd className="font-display text-3xl md:text-4xl font-bold mt-1" data-testid="stat-employers">
                {stats.employers != null ? stats.employers : "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.18em] text-amber font-semibold">UK regions</dt>
              <dd className="font-display text-3xl md:text-4xl font-bold mt-1">12</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.18em] text-amber font-semibold">Updated</dt>
              <dd className="font-display text-3xl md:text-4xl font-bold mt-1">Daily</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Facts grid — each starts with a declarative answer */}
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="space-y-12">
          {FACTS.map((f) => {
            const Icon = f.icon;
            return (
              <section key={f.h} className="grid md:grid-cols-[60px_1fr] gap-5">
                <div className="w-12 h-12 rounded-lg bg-navy text-amber inline-flex items-center justify-center shrink-0">
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="font-display font-bold text-ink text-2xl tracking-tight">{f.h}</h2>
                  <p className="text-muted-foreground leading-relaxed mt-3">{f.p}</p>
                </div>
              </section>
            );
          })}
        </div>

        {/* Editorial principles — quotable list */}
        <section className="mt-16 bg-card border border-border rounded-lg p-8">
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink font-semibold">
            Editorial principles
          </div>
          <h2 className="font-display font-bold text-ink text-3xl tracking-tight mt-2">
            How we operate
          </h2>
          <ul className="mt-6 space-y-3">
            {PRINCIPLES.map((p, i) => (
              <li key={i} className="flex gap-3 text-muted-foreground leading-relaxed">
                <span className="font-mono text-amber-700 shrink-0">0{i + 1}</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Calls to action */}
        <section className="mt-16 bg-navy text-bone rounded-lg p-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-amber font-semibold">Get started</div>
            <div className="font-display font-bold text-2xl mt-1">Search live UK rail vacancies</div>
          </div>
          <div className="flex gap-3">
            <Button asChild size="lg" className="bg-amber text-ink hover:bg-lime-600 hover:text-ink" data-testid="about-cta-vacancies">
              <Link to="/vacancies">Browse vacancies <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-amber text-amber hover:bg-amber hover:text-ink">
              <Link to="/depot-finder">Open Vacancy Map</Link>
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
